import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { nocService } from '../../services/apiService';

const NOCList = () => {
  const [nocRequests, setNocRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [isApprovalProcessExpanded, setIsApprovalProcessExpanded] = useState(false);

  useEffect(() => {
    fetchNOCRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const fetchNOCRequests = async () => {
    try {
      const data = await nocService.getNOCRequests({ status: filter });
      setNocRequests(data.nocRequests);
    } catch (error) {
      toast.error('Failed to fetch NOC requests');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PENDING: 'badge-pending',
      APPROVED: 'badge-approved',
      REJECTED: 'badge-rejected',
      CANCELLED: 'bg-slate-100 text-slate-800'
    };
    return styles[status] || 'bg-slate-100 text-slate-800';
  };

  const nocTypeLabels = {
    TRANSFER_CERTIFICATE: 'Transfer Certificate',
    COURSE_COMPLETION: 'Course Completion',
    INTERNSHIP: 'Internship',
    SCHOLARSHIP: 'Scholarship',
    EXAM_HALL_TICKET: 'Exam Hall Ticket',
    LIBRARY_CLEARANCE: 'Library Clearance',
    FEE_CLEARANCE: 'Fee Clearance',
    OTHER: 'Other'
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="py-2 sm:py-4">
      <div className="w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900 truncate">My NOC Requests</h1>
            <p className="text-xs sm:text-sm text-slate-600 mt-1">Manage your No Objection Certificate requests</p>
          </div>
          <Link
            to="/noc/create"
            className="w-full sm:w-auto px-4 sm:px-6 py-2.5 sm:py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors inline-flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-95 text-sm sm:text-base"
          >
            <span className="text-lg sm:text-xl">+</span>
            <span>New Request</span>
          </Link>
        </div>

        {/* Approval Flow Information */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg mb-4 sm:mb-6">
          <button
            onClick={() => setIsApprovalProcessExpanded(!isApprovalProcessExpanded)}
            className="w-full flex items-start gap-2 sm:gap-3 p-3 sm:p-4 text-left hover:bg-blue-100/50 transition-colors rounded-lg"
          >
            <span className="text-xl sm:text-2xl flex-shrink-0">ℹ️</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h4 className="font-semibold text-blue-900 text-sm sm:text-base">Sequential Approval Process</h4>
                <svg 
                  className={`w-5 h-5 text-blue-900 transition-transform flex-shrink-0 ${isApprovalProcessExpanded ? 'rotate-180' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              {!isApprovalProcessExpanded && (
                <p className="text-xs sm:text-sm text-blue-800 mt-1">
                  Click to view the approval workflow
                </p>
              )}
            </div>
          </button>
          
          {isApprovalProcessExpanded && (
            <div className="px-3 sm:px-4 pb-3 sm:pb-4 pt-0">
              <p className="text-xs sm:text-sm text-blue-800 mb-3 ml-8 sm:ml-11">
                All NOC requests must be approved in the following order:
              </p>
              
              {/* Approval Steps List */}
              <div className="space-y-2 ml-8 sm:ml-11">
                <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-lg border-l-4 border-blue-500 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold text-sm">
                    1
                  </div>
                  <div className="flex-1">
                    <p className="text-sm sm:text-base font-semibold text-slate-800">Accounts Section</p>
                    <p className="text-xs text-slate-500">Fee clearance verification</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-lg border-l-4 border-purple-500 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white flex items-center justify-center font-bold text-sm">
                    2
                  </div>
                  <div className="flex-1">
                    <p className="text-sm sm:text-base font-semibold text-slate-800">Library</p>
                    <p className="text-xs text-slate-500">Book return & dues clearance</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-lg border-l-4 border-indigo-500 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-indigo-500 text-white flex items-center justify-center font-bold text-sm">
                    3
                  </div>
                  <div className="flex-1">
                    <p className="text-sm sm:text-base font-semibold text-slate-800">Head of Department</p>
                    <p className="text-xs text-slate-500">Academic & departmental clearance</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-2 sm:p-3 bg-white rounded-lg border-l-4 border-green-500 shadow-sm">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-green-500 text-white flex items-center justify-center font-bold text-sm">
                    4
                  </div>
                  <div className="flex-1">
                    <p className="text-sm sm:text-base font-semibold text-slate-800">Exam Section</p>
                    <p className="text-xs text-slate-500">Final approval & certificate issuance</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 sm:mb-6 overflow-x-auto">
          <div className="flex gap-2 sm:gap-3 min-w-max sm:min-w-0 sm:flex-wrap">
            <button
              onClick={() => setFilter('')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                filter === '' 
                  ? 'bg-primary text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('PENDING')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                filter === 'PENDING' 
                  ? 'bg-primary text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              Pending
            </button>
            <button
              onClick={() => setFilter('APPROVED')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                filter === 'APPROVED' 
                  ? 'bg-primary text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              Approved
            </button>
            <button
              onClick={() => setFilter('REJECTED')}
              className={`px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap ${
                filter === 'REJECTED' 
                  ? 'bg-primary text-white shadow-md' 
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200 active:scale-95'
              }`}
            >
              Rejected
            </button>
          </div>
        </div>

        {/* NOC List */}
        {/* Info Banners for Students */}
        {filter === 'PENDING' && nocRequests.length > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-xl sm:text-2xl flex-shrink-0">⏳</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-blue-900 text-sm sm:text-base mb-1">Approval Process</h4>
                <p className="text-xs sm:text-sm text-blue-800">
                  Your request is being reviewed by multiple departments in sequence: 
                  <strong> Accounts → Library → HOD → Exam</strong>. 
                  Each must approve before the next reviews.
                </p>
              </div>
            </div>
          </div>
        )}
        
        {filter === 'REJECTED' && nocRequests.length > 0 && (
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-xl sm:text-2xl flex-shrink-0">💡</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-orange-900 text-sm sm:text-base mb-1">About Rejected Requests</h4>
                <p className="text-xs sm:text-sm text-orange-800">
                  <strong>Contact the department that rejected it</strong> to understand the reason. 
                  They may reconsider if you provide additional info.
                  <strong> You do NOT need to submit a new request.</strong>
                </p>
              </div>
            </div>
          </div>
        )}
        
        {filter === 'APPROVED' && nocRequests.length > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-3 sm:p-4 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3">
              <span className="text-xl sm:text-2xl flex-shrink-0">✅</span>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-green-900 text-sm sm:text-base mb-1">Approved Requests</h4>
                <p className="text-xs sm:text-sm text-green-800">
                  Congratulations! Approved by all departments. 
                  Click any request to <strong>download your NOC certificate</strong>.
                </p>
              </div>
            </div>
          </div>
        )}

        {nocRequests.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm p-6 sm:p-12 text-center">
            <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">📄</div>
            <h3 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2">No NOC Requests Found</h3>
            <p className="text-sm sm:text-base text-slate-600 mb-4 sm:mb-6">
              {filter ? 'No requests match your filter' : 'You haven\'t submitted any NOC requests yet'}
            </p>
            <Link
              to="/noc/create"
              className="inline-block w-full sm:w-auto px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors text-sm sm:text-base shadow-lg active:scale-95"
            >
              Create Your First Request
            </Link>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            {nocRequests.map((noc) => (
              <Link
                key={noc.id}
                to={`/noc/${noc.id}`}
                className="block bg-white rounded-lg shadow-sm hover:shadow-md transition-all p-4 sm:p-6 active:scale-[0.99]"
              >
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-2 sm:gap-4 mb-3 sm:mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-1">
                      {nocTypeLabels[noc.nocType]}
                    </h3>
                    <p className="text-xs sm:text-sm text-slate-600">
                      Submitted on {new Date(noc.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${getStatusBadge(noc.overallStatus)} self-start`}>
                    {noc.overallStatus}
                  </span>
                </div>

                <p className="text-sm sm:text-base text-slate-700 mb-3 sm:mb-4 line-clamp-2">{noc.reason}</p>

                {/* Rejection Notice */}
                {noc.overallStatus === 'REJECTED' && (() => {
                  const rejectedApproval = noc.approvals.find(a => a.status === 'REJECTED');
                  return rejectedApproval && rejectedApproval.remarks && (
                    <div className="mb-3 sm:mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <span className="text-base flex-shrink-0">❌</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-red-900 mb-1">
                            Rejected by {rejectedApproval.approverRole.replace('_', ' ')}
                          </p>
                          <p className="text-xs sm:text-sm text-red-800">
                            <span className="font-semibold">Reason:</span> {rejectedApproval.remarks}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Approval Progress */}
                <div className="flex gap-1.5 sm:gap-2 flex-wrap">
                  {(() => {
                    const approvalOrder = ['ACCOUNTS_SECTION', 'LIBRARY_STAFF', 'HOD', 'EXAM_SECTION'];
                    const sortedApprovals = [...noc.approvals].sort((a, b) => 
                      approvalOrder.indexOf(a.approverRole) - approvalOrder.indexOf(b.approverRole)
                    );
                    
                    const getRoleShortName = (role) => {
                      const roleMap = {
                        'ACCOUNTS_SECTION': 'Accounts',
                        'LIBRARY_STAFF': 'Library',
                        'EXAM_SECTION': 'Exam',
                        'HOD': 'HOD'
                      };
                      return roleMap[role] || role.replace('_', ' ');
                    };
                    
                    return sortedApprovals.map((approval, index) => (
                      <span
                        key={approval.id}
                        className={`px-2 py-1 rounded text-[10px] sm:text-xs font-medium ${
                          approval.status === 'APPROVED' 
                            ? 'bg-emerald-100 text-emerald-800' 
                            : approval.status === 'REJECTED'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-slate-100 text-slate-600'
                        }`}
                      >
                        {index + 1}. {getRoleShortName(approval.approverRole)}: {approval.status}
                      </span>
                    ));
                  })()}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default NOCList;
